import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Navbar, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { filterProvinces } from "@/lib/filterData";
import {
  MapPin, Truck, MessageSquare, ShoppingCart,
  Star, Phone, Mail, Loader2, Pencil, ChevronLeft, ChevronRight, ExternalLink, CheckCircle2,
} from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { toast } from "sonner";
import { useEffect, useRef, useState, lazy, Suspense } from "react";
import { BlurredImageContainer } from "@/components/BlurredImageContainer";
import { PurchaseModal } from "@/components/PurchaseModal";
import { SimilarListings } from "@/components/SimilarListings";

import { useJsonLd } from "@/hooks/useJsonLd";
import { useSeo } from "@/hooks/useSeo";

// Code-split the Leaflet map (and its CSS) — only loaded when this page renders
const StaticMap = lazy(() => import("@/components/StaticMap"));

const deliveryLabel: Record<string, string> = {
  hämta: "Hämta",
  leverans: "Leverans",
  båda: "Hämta / Leverans",
};
const categoryIcons: Record<string, string> = {
  ved: "🪵",
  julgran: "🎄",
  plankor: "📏",
  stockar: "🪓",
  träd: "🌳",
  byggnadsvirke: "🏗️",
  rundvirke: "🪵",
  pellets: "🔥",
  flis: "🌿",
  kutterspån: "🪚",
  sågspån: "🪚",
  snickerivirke: "🪑",
};

// Approximate coordinates for Swedish municipalities
const municipalityCoords: Record<string, [number, number]> = {
  Stockholm: [59.3293, 18.0686],
  Göteborg: [57.7089, 11.9746],
  Malmö: [55.604, 13.003],
  Uppsala: [59.8586, 17.6389],
  Linköping: [58.4108, 15.6214],
  Västerås: [59.6099, 16.5448],
  Örebro: [59.2753, 15.2134],
  Norrköping: [58.5942, 16.1826],
  Helsingborg: [56.0465, 12.6945],
  Jönköping: [57.7826, 14.1618],
  Umeå: [63.8258, 20.263],
  Lund: [55.7047, 13.191],
  Borås: [57.721, 12.9401],
  Sundsvall: [62.3908, 17.3069],
  Gävle: [60.6749, 17.1413],
  Falun: [60.6065, 15.6355],
  Karlstad: [59.3793, 13.5036],
  Luleå: [65.5848, 22.1547],
  Växjö: [56.8777, 14.8091],
  Halmstad: [56.6745, 12.8578],
  Kalmar: [56.6634, 16.3566],
  Kristianstad: [56.0294, 14.1567],
  Skellefteå: [64.7507, 20.9528],
  Karlskrona: [56.1616, 15.5866],
  Borlänge: [60.4858, 15.4375],
  Trollhättan: [58.2837, 12.2886],
  Östersund: [63.1792, 14.6357],
  Kiruna: [67.8558, 20.2253],
  Mora: [61.0043, 14.5448],
  Arvika: [59.6545, 12.5855],
};

function getProvinceForMunicipality(municipality: string): string | null {
  for (const p of filterProvinces) {
    if (p.municipalities.includes(municipality)) return p.name;
  }
  return null;
}

function getCoordsForLocation(municipality: string | null, location: string | null): [number, number] {
  if (municipality && municipalityCoords[municipality]) return municipalityCoords[municipality];
  if (location && municipalityCoords[location]) return municipalityCoords[location];
  // Default to Sweden center
  return [62.0, 15.0];
}

function StarRating({ rating, count }: { rating: number; count?: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`h-4 w-4 ${i <= Math.round(rating) ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/30"}`}
        />
      ))}
      {rating > 0 && <span className="ml-1 text-xs text-muted-foreground">({rating.toFixed(1)})</span>}
      {typeof count === "number" && count > 0 && (
        <span className="ml-1 text-xs text-muted-foreground">({count} {count === 1 ? "recension" : "recensioner"})</span>
      )}
    </div>
  );
}

// StaticMap is code-split (see import above)


export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Single round-trip: listing + seller profile + review aggregate
  const { data: detail, isLoading: listingLoading } = useQuery({
    queryKey: ["listing-detail", id, !!user],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc("get_listing_detail", { _id: id });
      if (error) throw error;
      return data as {
        listing: any;
        seller: any;
        review_count: number;
        avg_rating: number;
      } | null;
    },
    enabled: !!id,
  });

  const listing = detail?.listing ?? null;
  const sellerProfile = detail?.seller ?? null;
  const reviewCount = detail?.review_count ?? 0;
  const avgRating = Number(detail?.avg_rating ?? 0);

  // Legacy placeholder (kept so existing references compile); real email comes from profile
  const sellerEmail = sellerProfile?.full_name
    ? `${sellerProfile.full_name.toLowerCase().replace(/\s+/g, ".")}@example.com`
    : null;

  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);


  const images = listing?.images ?? [];
  const totalImages = images.length;

  useSeo({
    title: listing?.title ?? "Annons",
    description: listing
      ? (listing.description?.trim()
          ? `${listing.title} — ${listing.description.replace(/\s+/g, " ").trim()}`.slice(0, 160)
          : `${listing.title}${listing.location ? ` i ${listing.location}` : ""}${listing.price > 0 ? ` — ${listing.price.toLocaleString("sv-SE")} kr${listing.price_unit ? `/${listing.price_unit}` : ""}` : ""}. Köp direkt på TimberNet.`.slice(0, 160))
      : "Annons på TimberNet — Sveriges marknadsplats för virke och skogsprodukter.",
    path: `/annonser/${listing?.id ?? id ?? ""}`,
    ogType: "product",
  });

  useJsonLd(
    listing
      ? {
          "@context": "https://schema.org",
          "@type": "Product",
          name: listing.title,
          description: listing.description ?? undefined,
          image: images.length > 0 ? images : undefined,
          url: `https://timbernet.se/annonser/${listing.id ?? id}`,
          category: listing.category ?? listing.listing_type ?? undefined,
          offers:
            listing.price > 0
              ? {
                  "@type": "Offer",
                  price: listing.price,
                  priceCurrency: "SEK",
                  availability: "https://schema.org/InStock",
                  url: `https://timbernet.se/annonser/${listing.id ?? id}`,
                }
              : undefined,
          ...(reviewCount > 0 && avgRating > 0
            ? {
                aggregateRating: {
                  "@type": "AggregateRating",
                  ratingValue: avgRating,
                  reviewCount,
                },
              }
            : {}),
        }
      : null,
    "listing-product"
  );

  const requireAuth = (action: () => void) => {
    if (!user) {
      navigate("/logga-in");
      return;
    }
    action();
  };

  const province = listing?.municipality ? getProvinceForMunicipality(listing.municipality) : null;
  const coords = getCoordsForLocation(listing?.municipality ?? null, listing?.location ?? null);

  if (listingLoading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Navbar />
        <div className="container flex-1 py-8">
          <div className="grid gap-6 lg:grid-cols-8">
            <div className="lg:col-span-5 space-y-4">
              <div className="aspect-[4/3] w-full rounded-lg bg-muted animate-pulse" />
              <div className="h-6 w-2/3 rounded bg-muted animate-pulse" />
              <div className="h-4 w-1/2 rounded bg-muted animate-pulse" />
              <div className="h-4 w-3/4 rounded bg-muted animate-pulse" />
            </div>
            <div className="lg:col-span-3 space-y-4">
              <div className="h-32 w-full rounded-lg bg-muted animate-pulse" />
              <div className="h-10 w-full rounded bg-muted animate-pulse" />
              <div className="h-10 w-full rounded bg-muted animate-pulse" />
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="flex min-h-screen flex-col">
        <Navbar />
        <div className="container flex flex-1 items-center justify-center py-20">
          <div className="text-center">
            <p className="text-lg font-medium">Annonsen hittades inte</p>
            <BackButton fallback="/annonser" />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Build specs based on listing type
  const specs: { label: string; value: string }[] = [];
  const listingType = listing.listing_type?.toLowerCase() ?? listing.category?.toLowerCase();

  if (listing.species) specs.push({ label: "Trädslag", value: listing.species });
  if (listing.wood_age) specs.push({ label: "Ålder", value: `${listing.wood_age} år` });

  if (listingType === "träd") {
    if (listing.diameter) specs.push({ label: "Diameter", value: `${listing.diameter} cm` });
    if (listing.quantity) specs.push({ label: "Antal", value: `${listing.quantity} träd` });
  } else if (listingType === "plankor" || listingType === "byggnadsvirke" || listingType === "snickerivirke") {
    if (listing.moisture_content) specs.push({ label: "Fuktkvot", value: listing.moisture_content });
    if (listing.processing_grade) specs.push({ label: "Bearbetningsgrad", value: listing.processing_grade });
    const l = (listing as any).plank_length_cm;
    const w = (listing as any).plank_width_cm;
    const h = (listing as any).plank_height_cm;
    if (l != null && w != null && h != null) {
      specs.push({ label: "Dimensioner (L×B×H)", value: `${l}×${w}×${h} cm` });
    }
    if (listing.quantity) specs.push({ label: "Antal", value: `${listing.quantity} plankor` });
  } else if (listingType === "ved") {
    if (listing.moisture_content) specs.push({ label: "Fuktkvot", value: listing.moisture_content });
    if (listing.quantity) specs.push({ label: "Mängd", value: `${listing.quantity} kubik` });
  } else {
    // Generic fallback
    if (listing.diameter) specs.push({ label: "Diameter", value: `${listing.diameter} cm` });
    if (listing.moisture_content) specs.push({ label: "Fuktkvot", value: listing.moisture_content });
    if (listing.processing_grade) specs.push({ label: "Bearbetningsgrad", value: listing.processing_grade });
    if (listing.quantity) specs.push({ label: "Antal", value: `${listing.quantity}` });
  }

  if (listing.municipality && province) {
    specs.push({ label: "Kommun & Län", value: `${listing.municipality}, ${province}` });
  } else if (listing.municipality) {
    specs.push({ label: "Kommun", value: listing.municipality });
  } else if (listing.location) {
    specs.push({ label: "Plats", value: listing.location });
  }

  const imageUrl = images.length > 0 ? images[activeImageIndex] : null;

  return (
    <div className="flex min-h-screen w-full max-w-full flex-col overflow-x-hidden">
      <Navbar />
      <div className="flex-1 w-full max-w-full overflow-x-hidden px-4 py-8 sm:px-6 lg:container">
        <BackButton fallback="/annonser" label="Tillbaka till marknadsplatsen" className="mb-6 text-muted-foreground" />

        <div className="grid max-w-full gap-8 overflow-hidden lg:grid-cols-8 lg:items-start">
          {/* Main content */}
          <div className="min-w-0 max-w-full lg:col-span-5">
            {/* Image with navigation */}
            {imageUrl ? (
              <div className="relative mb-6 w-full max-w-full overflow-hidden rounded-lg">
                <BlurredImageContainer
                  src={imageUrl}
                  alt={listing.title}
                  className="rounded-lg"
                />
                {/* Left arrow */}
                {activeImageIndex > 0 && (
                  <button
                    onClick={() => setActiveImageIndex((prev) => prev - 1)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-foreground/40 text-background backdrop-blur-sm hover:bg-foreground/60 transition-colors"
                    aria-label="Föregående bild"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                )}
                {/* Right arrow */}
                {activeImageIndex < totalImages - 1 && (
                  <button
                    onClick={() => setActiveImageIndex((prev) => prev + 1)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-foreground/40 text-background backdrop-blur-sm hover:bg-foreground/60 transition-colors"
                    aria-label="Nästa bild"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                )}
              </div>
            ) : (
              <div className="mb-6 flex items-center justify-center rounded-lg bg-secondary text-6xl" style={{ aspectRatio: "4/3" }}>
                {categoryIcons[listing.category?.toLowerCase()] ?? "🌲"}
              </div>
            )}

            {/* Clickable thumbnail strip */}
            {images.length > 1 && (
              <div className="mb-6 flex max-w-full gap-3 overflow-x-auto px-0 pb-2 py-[10px]">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImageIndex(i)}
                    className={`h-20 w-20 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-all ${
                      i === activeImageIndex
                        ? "border-primary ring-2 ring-primary/30"
                        : "border-transparent hover:border-muted-foreground/30"
                    }`}
                  >
                    <img
                      src={img}
                      alt={`${listing.title} ${i + 1}`}
                      className="h-full w-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}

              <div className="mb-4 flex max-w-full flex-wrap gap-2">
              <Badge variant="secondary" className="capitalize">{listing.category}</Badge>
              <Badge variant="outline" className="capitalize">{listing.species}</Badge>
              {listing.listing_type && (
                <Badge variant="outline" className="capitalize">{listing.listing_type}</Badge>
              )}
            </div>

            <div className="mb-2 flex max-w-full flex-wrap items-start gap-3">
              <h1 className="min-w-0 break-words font-display text-2xl font-bold md:text-3xl">{listing.title}</h1>
              {listing.status === "såld" && (
                <Badge variant="secondary" className="bg-muted text-muted-foreground">Såld</Badge>
              )}
              {user && user.id === listing.seller_id && (
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/annonser/${listing.id}/redigera`}>
                    <Pencil className="mr-1 h-3.5 w-3.5" /> Redigera
                  </Link>
                </Button>
              )}
            </div>

            <div className="mb-6 flex max-w-full flex-wrap gap-4 text-sm text-muted-foreground">
              {(listing.municipality || listing.location) && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" /> {listing.municipality ?? listing.location}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Truck className="h-4 w-4" /> {deliveryLabel[listing.delivery] ?? listing.delivery}
              </span>
            </div>

            {listing.description && (
              <div className="max-w-full overflow-hidden rounded-lg border border-border bg-card p-6">
                <h2 className="mb-3 text-lg font-semibold">Beskrivning</h2>
                <p className="max-w-full break-words text-sm leading-relaxed text-muted-foreground">{listing.description}</p>
              </div>
            )}

          </div>

          {/* Sidebar */}
          <div className="min-w-0 max-w-full space-y-4 lg:col-span-3">
            <div className="sticky top-20 max-w-full space-y-4">
              {/* Container 1: Price > Specs > Buy > Message */}
              <div className="max-w-full overflow-hidden rounded-lg border border-border bg-card p-6">
                {/* Price */}
                {listing.price > 0 ? (
                  <>
                    <p className="mb-1 text-2xl font-bold text-heading">
                      {listing.price.toLocaleString("sv-SE")} kr
                    </p>
                    {listing.price_unit && (
                      <p className="text-xs text-muted-foreground">{listing.price_unit}</p>
                    )}
                  </>
                ) : (
                  <p className="text-lg font-semibold text-accent">Prisförslag välkomnas</p>
                )}

                {specs.length > 0 && (
                  <>
                    <Separator className="my-4" />
                    <h3 className="mb-3 text-sm font-semibold text-foreground">Specifikationer</h3>
                    <div className="space-y-0">
                      {specs.map((spec, i) => (
                        <div key={spec.label}>
                          <div className="flex items-start justify-between gap-4 py-2">
                            <span className="min-w-0 text-sm text-muted-foreground">{spec.label}</span>
                            <span className="min-w-0 break-words text-right text-sm font-medium text-foreground">{spec.value}</span>
                          </div>
                          {i < specs.length - 1 && <Separator />}
                        </div>
                      ))}
                    </div>
                  </>
                )}

                <Separator className="my-4" />

                {(() => {
                  const adminUploaded = (listing as any).uploaded_by_admin === true;
                  const platform = (listing as any).platform_source as string | null;
                  const externalUrl = (listing as any).external_url as string | null;
                  const isExternal = adminUploaded && (platform === "blocket" || platform === "fb_marketplace");
                  const externalLabel = platform === "blocket" ? "Se på Blocket" : "Se på Facebook Marketplace";
                  const sourceLabel = platform === "blocket"
                    ? "Den här annonsen är hämtad från Blocket"
                    : platform === "fb_marketplace"
                      ? "Den här annonsen är hämtad från Facebook Marketplace"
                      : null;

                  if (isExternal) {
                    return (
                      <>
                        <Button
                          asChild
                          className="w-full bg-primary hover:bg-primary/90"
                          size="lg"
                          disabled={!externalUrl}
                        >
                          <a href={externalUrl ?? "#"} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="mr-2 h-4 w-4" /> {externalLabel}
                          </a>
                        </Button>
                        {sourceLabel && (
                          <p className="mt-2 text-xs text-muted-foreground text-center">{sourceLabel}</p>
                        )}
                      </>
                    );
                  }

                  return (
                    <>
                      <Button
                        className="w-full bg-primary hover:bg-primary/90"
                        size="lg"
                        disabled={listing.status === "såld"}
                        onClick={() => requireAuth(() => setPurchaseOpen(true))}
                      >
                        <ShoppingCart className="mr-2 h-4 w-4" /> {listing.status === "såld" ? "Såld" : "Köp"}
                      </Button>

                      <Button
                        variant="outline"
                        className="mt-2 w-full"
                        onClick={() => {
                          
                          requireAuth(() => {
                            navigate(`/meddelanden/listing-${listing.id}-${user!.id}`);
                          });
                        }}
                      >
                        <MessageSquare className="mr-2 h-4 w-4" /> Skicka meddelande
                      </Button>
                    </>
                  );
                })()}

              </div>

              {/* Shipping options */}
              {(() => {
                const pickup = (listing as any).pickup_enabled ?? true;
                const shipping = (listing as any).shipping_enabled ?? false;
                if (!pickup && !shipping) return null;
                return (
                  <div className="max-w-full overflow-hidden rounded-lg border border-border bg-card p-6">
                    
                    <div className={`grid gap-3 ${pickup && shipping ? "grid-cols-2" : "grid-cols-1"}`}>
                      {pickup && (
                        <div className="flex flex-col items-center justify-start rounded-lg border border-border bg-background p-4 text-center">
                          <MapPin className="mb-2 h-7 w-7 text-primary" />
                          <p className="mb-1 text-sm font-semibold text-foreground">Hämtas på plats</p>
                          <p className="text-xs text-muted-foreground">Upphämtnings adress skickas vid accepterat köpförslag</p>
                        </div>
                      )}
                      {shipping && (
                        <div className="flex flex-col items-center justify-start rounded-lg border border-border bg-background p-4 text-center">
                          <Truck className="mb-2 h-7 w-7 text-primary" />
                          <p className="mb-1 text-sm font-semibold text-foreground">Kan fraktas</p>
                          <p className="text-xs text-muted-foreground">Kan fraktas av säljare</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Container 2: Profile / Admin uploaded */}
              {(listing as any).uploaded_by_admin === true ? (
                <div className="max-w-full overflow-hidden rounded-lg border border-border bg-card p-6">
                  <div className="flex items-center gap-3">
                    <img src="/app-icon.png" alt="Timbernet logotyp" className="h-10 w-10 rounded-md" />
                    <p className="text-sm font-semibold text-foreground">Uppladdad av Timbernet</p>
                  </div>
                  <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Denna annons har laddas up av en administratör
                  </div>
                </div>
              ) : (
                <div className="max-w-full overflow-hidden rounded-lg border border-border bg-card p-6">
                  {sellerProfile && (
                    <>
                      <Link
                        to={`/saljare/${sellerProfile?.username || listing.seller_id}`}
                        className="flex items-center gap-3 group"
                      >
                        <Avatar className="h-12 w-12 flex-shrink-0">
                          {sellerProfile.avatar_url ? (
                            <AvatarImage src={sellerProfile.avatar_url} alt={sellerProfile.full_name} />
                          ) : null}
                          <AvatarFallback className="bg-muted text-muted-foreground font-semibold">
                            {sellerProfile.full_name
                              .split(" ")
                              .map((n) => n[0])
                              .join("")
                              .toUpperCase()
                              .slice(0, 2)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                            {sellerProfile.full_name}
                          </p>
                          <div className="mt-0.5 flex items-center gap-1.5">
                            <div className="flex items-center gap-0.5">
                              {[1, 2, 3, 4, 5].map((i) => (
                                <Star
                                  key={i}
                                  className={`h-3.5 w-3.5 ${i <= Math.round(avgRating) ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/30"}`}
                                />
                              ))}
                            </div>
                            {reviewCount > 0 && (
                              <span className="text-xs text-muted-foreground">{reviewCount}</span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
                      </Link>

                      <Separator className="my-4" />

                      <div className="space-y-2 text-sm text-muted-foreground">
                        {(listing.municipality || sellerProfile.location) && (
                          <p className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 flex-shrink-0" />
                            <span className="min-w-0 break-words">
                              {province && listing.municipality
                                ? `${listing.municipality}, ${province}`
                                : sellerProfile.location ?? listing.municipality}
                            </span>
                          </p>
                        )}
                      </div>

                      {user && sellerProfile.phone && (
                        <>
                          <Separator className="my-4" />
                          <div className="space-y-1.5 text-sm">
                            <p className="flex max-w-full items-start gap-2 text-muted-foreground">
                              <Phone className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                              <span className="min-w-0 break-words">{sellerProfile.phone}</span>
                            </p>
                          </div>
                        </>
                      )}
                      {!user && (
                        <>
                          <Separator className="my-4" />
                          <p className="text-xs text-muted-foreground italic">
                            Logga in för att se kontaktuppgifter
                          </p>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Add-on services */}
      <div className="w-full max-w-full px-4 sm:px-6 lg:container lg:mx-auto py-8">
        <div className="max-w-full overflow-hidden rounded-lg border border-border bg-card p-6">
          <h2 className="mb-3 text-lg font-semibold">Behöver du tilläggstjänster?</h2>
          <p className="mb-4 max-w-full break-words text-sm text-muted-foreground">
            Behöver du fällning, transport eller sågning? Skicka en förfrågan så matchar vi dig med lokala leverantörer.
          </p>
          <Button
            variant="outline"
            onClick={() =>
              requireAuth(() => toast.info("Förfrågan skickad! Leverantörer i området blir notifierade."))
            }
          >
            Begär offert för tilläggstjänster
          </Button>
        </div>
      </div>
      <SimilarListings listing={listing} />
      <Footer />

      {listing && user && (
        <PurchaseModal
          open={purchaseOpen}
          onOpenChange={setPurchaseOpen}
          listing={{
            id: listing.id,
            title: listing.title,
            price: listing.price,
            price_unit: listing.price_unit,
            quantity: listing.quantity,
            seller_id: listing.seller_id,
            pickup_enabled: (listing as any).pickup_enabled ?? true,
            shipping_enabled: (listing as any).shipping_enabled ?? false,
            shipping_price: (listing as any).shipping_price ?? null,
            delivery_radius_km: (listing as any).delivery_radius_km ?? null,
            location: listing.location ?? null,
            municipality: (listing as any).municipality ?? null,
          }}
          buyerId={user.id}
        />
      )}
    </div>
  );
}
